import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Award, Flame, Sparkles, ArrowRight } from "lucide-react";
import { getMyXp } from "@/lib/xp.functions";
import { getMyStreak } from "@/lib/streak.functions";
import { getUserAchievements } from "@/lib/achievements.functions";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/settings/performance")({
  head: () => ({
    meta: [
      { title: "Desempenho — Viny Club" },
      {
        name: "description",
        content: "Seus níveis, conquistas e sequência de atividade em um só lugar.",
      },
    ],
  }),
  component: SettingsPerformance,
});

function SettingsPerformance() {
  const { profile, isAuthenticated } = useCurrentUser();
  const fetchXp = useServerFn(getMyXp);
  const fetchStreak = useServerFn(getMyStreak);
  const fetchAchievements = useServerFn(getUserAchievements);

  const xpQ = useQuery({ queryKey: ["my-xp"], queryFn: () => fetchXp(), enabled: isAuthenticated });
  const streakQ = useQuery({
    queryKey: ["my-streak"],
    queryFn: () => fetchStreak(),
    enabled: isAuthenticated,
  });
  const achQ = useQuery({
    queryKey: ["my-achievements", profile?.id],
    queryFn: () => fetchAchievements({ data: { userId: profile!.id } }),
    enabled: !!profile?.id,
  });

  const level = xpQ.data?.level ?? 1;
  const xp = xpQ.data?.xp ?? 0;
  const nextLevelXp = xpQ.data?.nextLevelXp ?? 50;
  const prevLevelXp = xpQ.data?.prevLevelXp ?? 0;
  const progress = Math.max(
    0,
    Math.min(100, ((xp - prevLevelXp) / Math.max(1, nextLevelXp - prevLevelXp)) * 100),
  );
  const earned = (achQ.data ?? []).filter((a) => a.earned).length;
  const total = (achQ.data ?? []).length;

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-[13.5px] text-muted-foreground">
        Níveis, conquistas e sequência de atividade — tudo que mede o seu ritmo na Viny.
      </p>

      <div className="grid gap-3 md:grid-cols-3">
        <Card
          to="/levels"
          icon={<Sparkles className="h-4 w-4 text-[color:var(--action)]" />}
          label="Níveis e XP"
          value={`Nível ${level}`}
          hint={`${xp} XP · faltam ${Math.max(0, nextLevelXp - xp)} para o próximo`}
        >
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[color:var(--action)]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </Card>

        <Card
          to="/conquistas"
          icon={<Award className="h-4 w-4 text-amber-400" />}
          label="Conquistas"
          value={total ? `${earned}/${total}` : "—"}
          hint="medalhas desbloqueadas"
        >
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-amber-400"
              style={{ width: `${total ? (earned / total) * 100 : 0}%` }}
            />
          </div>
        </Card>

        <Card
          to="/streaks"
          icon={<Flame className="h-4 w-4 text-orange-400" />}
          label="Sequência"
          value={`${streakQ.data?.current ?? 0} ${(streakQ.data?.current ?? 0) === 1 ? "dia" : "dias"}`}
          hint={`recorde de ${streakQ.data?.longest ?? 0} dias`}
        >
          <div className="mt-3 flex gap-1">
            {(streakQ.data?.days ?? []).slice(-28).map((d) => (
              <span
                key={d.date}
                className={
                  "h-4 flex-1 rounded-[3px] " + (d.active ? "bg-orange-400/80" : "bg-white/8")
                }
              />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({
  to,
  icon,
  label,
  value,
  hint,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  children?: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-4 transition-colors hover:border-foreground/25 hover:bg-surface/70"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-muted-foreground">
          {icon} {label}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <div className="mt-3 font-display text-2xl font-semibold leading-none">{value}</div>
      <div className="mt-1.5 text-[12px] text-muted-foreground">{hint}</div>
      {children}
    </Link>
  );
}
